/**
 * Quote queue (#2652) — the follow-up list for quotations, shown as the
 * Quotes tab of the work orders page (#3788).
 *
 * Every quote sits in one bucket (accepted / expiring / unsent / awaiting /
 * expired / rejected, see `useQuoteQueue`). The default view is every open
 * bucket in working order, so the staff can go through them one after another
 * and see which customers to chase. The state is the first column, with a line
 * saying what it means for the quote. A row opens the work order, where the
 * quote is sent (#2651) or scheduled (#2647).
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import {
  DataTable,
  type DataTableColumn,
  EntityListResults,
  RegistrationPlate,
  VehicleMakeLogo,
} from '@/components/common';
import { useDebounce } from '@/hooks/useDebounce';
import { useUrlSearchParam } from '@/hooks/useUrlSearchParam';
import {
  QUOTE_QUEUE_BUCKETS,
  QUOTE_QUEUE_SORT_FIELDS,
  usePagedQuoteQueue,
  useQuoteQueueCounts,
  type QuoteQueueBucket,
  type QuoteQueueFilters,
  type QuoteQueueItem,
  type QuoteQueueSortField,
} from '@/hooks/useQuoteQueue';
import { useOpenWorkOrder, useViewedWorkOrderId } from '@/hooks/useWorkOrderWindow';
import { dateFnsLocale } from '@/utils/locale';

type BucketFilter = QuoteQueueBucket | 'open';
const BUCKET_FILTERS: readonly BucketFilter[] = ['open', ...QUOTE_QUEUE_BUCKETS];

/** Module scope, so colours and keys stay with the bucket (labelKey pattern). */
const BUCKET_CONFIG: Record<QuoteQueueBucket, { labelKey: string; color: 'success' | 'warning' | 'info' | 'primary' | 'error' | 'default' }> = {
  accepted: { labelKey: 'quoteQueue.buckets.accepted', color: 'success' },
  expiring: { labelKey: 'quoteQueue.buckets.expiring', color: 'warning' },
  unsent: { labelKey: 'quoteQueue.buckets.unsent', color: 'info' },
  awaiting: { labelKey: 'quoteQueue.buckets.awaiting', color: 'primary' },
  expired: { labelKey: 'quoteQueue.buckets.expired', color: 'error' },
  rejected: { labelKey: 'quoteQueue.buckets.rejected', color: 'default' },
};

const SORT_FIELDS = new Set<string>(QUOTE_QUEUE_SORT_FIELDS);

function isBucket(value: string | null): value is QuoteQueueBucket {
  return value !== null && (QUOTE_QUEUE_BUCKETS as readonly string[]).includes(value);
}

function formatDate(iso: string): string {
  return format(parseISO(iso), 'd.M.yyyy', { locale: dateFnsLocale() });
}

export function QuoteBucketChip({ bucket, size = 'small' }: { bucket: QuoteQueueBucket; size?: 'small' | 'medium' }) {
  const { t } = useTranslation('workorders');
  const config = BUCKET_CONFIG[bucket];
  return <Chip label={t(config.labelKey)} color={config.color} size={size} variant={bucket === 'rejected' ? 'outlined' : 'filled'} />;
}

export function QuoteQueueTab() {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const openWorkOrder = useOpenWorkOrder();
  const viewedWorkOrderId = useViewedWorkOrderId();
  // Bucket and search live in the URL so Back restores them (#4100). The
  // search is `quoteQ`: the work order list on the other tab owns `q`.
  const [bucketParam, setBucketParam] = useUrlSearchParam('bucket', { immediate: true });
  const selectedBucket: BucketFilter = isBucket(bucketParam) ? bucketParam : 'open';
  const [searchQuery, setSearchQuery] = useUrlSearchParam('quoteQ');
  const debouncedQuery = useDebounce(searchQuery, 300);

  const filters = useMemo<QuoteQueueFilters>(() => {
    const next: QuoteQueueFilters = {};
    if (selectedBucket !== 'open') next.bucket = selectedBucket;
    if (debouncedQuery.trim()) next.q = debouncedQuery.trim();
    return next;
  }, [selectedBucket, debouncedQuery]);

  const paged = usePagedQuoteQueue(filters);
  const { data: counts } = useQuoteQueueCounts();

  const selectBucket = useCallback((bucket: BucketFilter) => {
    setBucketParam(bucket === 'open' ? '' : bucket);
  }, [setBucketParam]);

  const handleSortChange = useCallback((sortKey: string, direction: 'asc' | 'desc') => {
    if (!SORT_FIELDS.has(sortKey)) return;
    paged.setSort({ sortBy: sortKey as QuoteQueueSortField, sortDir: direction });
  }, [paged]);

  const today = new Date();

  const validity = (item: QuoteQueueItem) => {
    if (item.quoteValidityDays != null && !item.quoteExpiresAt) {
      return (
        <Typography variant="body2">
          {t('quoteQueue.validForDaysFromSend', { count: item.quoteValidityDays })}
        </Typography>
      );
    }
    if (!item.quoteExpiresAt) {
      return <Typography variant="body2" color="text.secondary">{t('quoteQueue.noExpiry')}</Typography>;
    }
    const days = differenceInCalendarDays(parseISO(item.quoteExpiresAt), today);
    const caption = days < 0
      ? t('quoteQueue.expiredDaysAgo', { count: -days })
      : days === 0
        ? t('quoteQueue.expiresToday')
        : t('quoteQueue.daysLeft', { count: days });
    return (
      <>
        <Typography variant="body2">{formatDate(item.quoteExpiresAt)}</Typography>
        <Typography variant="caption" color={days < 0 ? 'error.main' : days <= 7 ? 'warning.main' : 'text.secondary'}>
          {caption}
        </Typography>
      </>
    );
  };

  const sentInfo = (item: QuoteQueueItem) => {
    const send = item.latestSend;
    if (!send) {
      return <Typography variant="body2" color="text.secondary">{t('quoteQueue.notSent')}</Typography>;
    }
    const channel = send.sentVia === 'whatsapp' ? t('sendQuote.channelWhatsapp') : t('sendQuote.channelEmail');
    const waitingDays = differenceInCalendarDays(today, parseISO(send.sentAt));
    return (
      <>
        <Typography variant="body2">{formatDate(send.sentAt)}</Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {channel}: {send.recipient}
        </Typography>
        {!send.respondedAt && (
          <Typography variant="caption" color={waitingDays >= 7 ? 'warning.main' : 'text.secondary'}>
            {t('quoteQueue.waitingDays', { count: waitingDays })}
            {send.reminderCount > 0 ? ` · ${t('quoteQueue.reminders', { count: send.reminderCount })}` : ''}
          </Typography>
        )}
      </>
    );
  };

  /** The bucket chip, and for an answered quote when and why the customer answered. */
  const stateCell = (item: QuoteQueueItem) => {
    const send = item.latestSend;
    const answered = send?.respondedAt ? send : null;
    const note = answered?.rejectionReason ?? answered?.customerComment ?? null;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        <QuoteBucketChip bucket={item.bucket} />
        {answered?.respondedAt && answered.response && (
          <Typography variant="caption" color="text.secondary">
            {t(
              answered.response === 'accepted'
                ? 'quoteQueue.acceptedOn'
                : answered.response === 'partial'
                  ? 'quoteQueue.acceptedPartialOn'
                  : 'quoteQueue.rejectedOn',
              { date: formatDate(answered.respondedAt) },
            )}
          </Typography>
        )}
        {note && (
          <Tooltip title={note}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, fontStyle: 'italic' }}>
              {note}
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  };

  const columns: DataTableColumn<QuoteQueueItem>[] = [
    {
      header: t('statusLabel'),
      sortKey: 'priority',
      width: 190,
      renderCell: stateCell,
    },
    {
      header: t('quoteQueue.columns.number'),
      sortKey: 'workOrderNumber',
      width: 110,
      renderCell: (item) => (
        <Typography variant="body2" fontWeight={600}>{item.workOrderNumber ?? '—'}</Typography>
      ),
    },
    {
      header: t('vehicle'),
      renderCell: (item) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <VehicleMakeLogo make={item.vehicleMake} size={32} />
          <Box>
            <RegistrationPlate registrationNumber={item.vehicleRegNumber} />
            <Typography variant="caption" color="text.secondary" display="block">
              {[item.vehicleMake, item.vehicleModel].filter(Boolean).join(' ')}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      header: t('customer'),
      renderCell: (item) => (
        <>
          <Typography variant="body2" fontWeight={500}>{item.customerName || '—'}</Typography>
          {item.customerPhone && (
            <Typography variant="caption" color="text.secondary">{item.customerPhone}</Typography>
          )}
        </>
      ),
    },
    {
      header: t('services'),
      renderCell: (item) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{item.serviceNames || '—'}</Typography>
      ),
    },
    {
      header: t('quoteQueue.columns.validUntil'),
      sortKey: 'quoteExpiresAt',
      renderCell: validity,
    },
    {
      header: t('quoteQueue.columns.sent'),
      sortKey: 'sentAt',
      renderCell: sentInfo,
    },
  ];

  const renderMobileCard = (item: QuoteQueueItem) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <RegistrationPlate registrationNumber={item.vehicleRegNumber} />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{item.workOrderNumber ?? ''}</Typography>
      </Box>
      <Box sx={{ mb: 1 }}>{stateCell(item)}</Box>
      <Typography variant="body2" fontWeight={500}>{item.customerName || '—'}</Typography>
      <Typography variant="body2" color="text.secondary" noWrap>{item.serviceNames || '—'}</Typography>
      <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
        <Box>{validity(item)}</Box>
        <Box>{sentInfo(item)}</Box>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder={t('quoteQueue.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: '100%', md: 420 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Tooltip title={t('clearSearch')}>
                    <IconButton size="small" onClick={() => setSearchQuery('')} aria-label={t('clearSearch')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {BUCKET_FILTERS.map((bucket) => {
          const selected = bucket === selectedBucket;
          const count = counts ? counts[bucket] : undefined;
          const label = bucket === 'open' ? t('quoteQueue.buckets.open') : t(BUCKET_CONFIG[bucket].labelKey);
          const color = bucket === 'open' ? 'default' : BUCKET_CONFIG[bucket].color;
          return (
            <Tooltip key={bucket} title={label}>
              <Chip
                label={count === undefined ? label : `${label} · ${count}`}
                size="small"
                color={selected ? (color === 'default' ? 'primary' : color) : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => selectBucket(bucket)}
                sx={{ fontWeight: selected ? 600 : 400 }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {paged.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void paged.refetch()}>
              {tc('errors.refresh')}
            </Button>
          }
        >
          {t('quoteQueue.errorLoading')}
        </Alert>
      ) : (
        <EntityListResults
          loading={paged.isLoading}
          resultCountText={t('quoteQueue.resultCount', { count: paged.total })}
          pagination={paged.pagination}
        >
          <DataTable<QuoteQueueItem>
            columns={columns}
            data={paged.rows}
            getRowKey={(item) => item.id}
            sortKey={paged.sort?.sortBy ?? 'priority'}
            sortDirection={paged.sort?.sortDir ?? 'asc'}
            onSortChange={handleSortChange}
            emptyMessage={t('quoteQueue.empty')}
            emptyDescription={t('quoteQueue.emptyDesc')}
            loading={paged.isLoading}
            renderMobileCard={renderMobileCard}
            onRowClick={(item, event) => { openWorkOrder(item.id, event); }}
            selectedRowKey={viewedWorkOrderId}
          />
        </EntityListResults>
      )}
    </Box>
  );
}
